/**
 * Validation API client (Sprint 6).
 *
 * Shapes mirror backend/src/modules/validation/routes/validation-routes.ts.
 * All requests go through apiRequest which attaches auth and handles
 * the { ok, data } envelope.
 */

import { apiRequest } from './client';
import type {
  FindingDTO,
  LoanFileValidationView,
  Paginated,
  RuleDefinitionDTO,
} from '@loanaudit/shared';

export interface FindingsListQuery {
  page: number;
  pageSize: number;
  loanFileId?: string;
  ucId?: 'uc1' | 'uc2' | 'uc3';
  ruleId?: string;
  status?: 'open' | 'accepted' | 'waived' | 'false_positive' | 'superseded';
  severity?: 'critical' | 'high' | 'medium' | 'low' | 'info';
  verdictSource?: 'deterministic' | 'llm' | 'both_agree' | 'both_disagree';
  latestOnly?: boolean;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  /** Sort on loan number first so each loan's findings stay contiguous. */
  groupByLoan?: boolean;
}

export interface RunValidationBody {
  ucIds?: Array<'uc1' | 'uc2' | 'uc3'>;
  llmEnabled?: boolean;
  reason?: string;
}

export interface ResolveFindingBody {
  action: 'accept' | 'waive' | 'false_positive';
  notes?: string;
}

export interface FindingDetailResponse {
  finding: FindingDTO & { loanNumber: string };
  pairedFinding: FindingDTO | null;
}

export const validationApi = {
  getLoanFileView: (loanFileId: string) =>
    apiRequest<LoanFileValidationView>(`/api/validation/loan-files/${loanFileId}`),

  runValidation: (loanFileId: string, body: RunValidationBody = {}) =>
    apiRequest<{ enqueued: boolean; jobId: string; loanFileId: string }>(
      `/api/validation/loan-files/${loanFileId}/run`,
      { method: 'POST', body },
    ),

  listFindings: (query: FindingsListQuery) =>
    apiRequest<Paginated<FindingDTO & { loanNumber: string }>>(
      `/api/validation/findings`,
      { query: query as unknown as Record<string, string | number | boolean | undefined> },
    ),

  getFinding: (findingId: string) =>
    apiRequest<FindingDetailResponse>(`/api/validation/findings/${findingId}`),

  resolveFinding: (findingId: string, body: ResolveFindingBody) =>
    apiRequest<{ id: string; status: string }>(
      `/api/validation/findings/${findingId}/resolve`,
      { method: 'POST', body },
    ),

  /** Bulk-resolve all open findings (optionally scoped to one loan file). */
  resolveAllFindings: (body: { loanFileId?: string; action?: 'accept' | 'waive' | 'false_positive'; notes?: string } = {}) =>
    apiRequest<{ count: number }>(`/api/validation/findings/resolve-all`, {
      method: 'POST',
      body,
    }),

  listRules: () =>
    apiRequest<{ items: RuleDefinitionDTO[]; total: number }>(
      `/api/validation/rules`,
    ),
};
